import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../services/api';
import Select from "react-select";
import { useLoading, usePersonChanged, useVisaAktuell, useImpersonatedUserId, useStation } from '../contexts/GlobalStateContext';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';

const PersonalForm = () => {
  const { station } = useStation();
  const { impersonatedUserId } = useImpersonatedUserId();
  const [personer, setPersoner] = useState([]);
  const [allaPersoner, setAllaPersoner] = useState([]);
  const [aktuellaPersoner, setAktuellaPersoner] = useState([]);
  const [roller, setRoller] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'namn', direction: 'asc' });
  const [savingPersonId, setSavingPersonId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState(0);
  const { setLoading } = useLoading();
  const { visaAktuell, setVisaAktuell } = useVisaAktuell();
  const { personChanged, setPersonChanged } = usePersonChanged();
  const [visaAllaStationer, setVisaAllaStationer] = useState(false);

  useEffect(() => { getRoller(); }, []);
  useEffect(() => { fetchPersoner(station); }, [station, visaAktuell, personChanged, visaAllaStationer]);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data?.type === 'downloadExcel') {
        await downloadKommandePassExcel(event.data.personId);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [impersonatedUserId, station]);

  const downloadKommandePassExcel = useCallback(async (personId) => {
    setLoading(true)
    try {
      let urlLink = '/api/reports/excel2';
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        body: JSON.stringify({
          type: 'kommandepass',
          personId: personId,
          station: station
        })
      });

      if (!response.ok) {
        const msg = `❌ Fel vid nedladdning av månadsrapport`;
        toast.error(msg);
        return;
      }

      //let fileName = selectedMonth.replace(/[/\\?%*:|"<> ]/g, '_') + '.xlsx';
      let fileName = 'KommandePass.xlsx';

      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        // Extrahera filnamnet från headern
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      const msg = error.msg || `❌ Fel vid nedladdning av Kommande Pass rapport`;
      toast.error(msg);
    } finally {
      setLoading(false)
    }
  }, [selectedPersonId, station]);

  const getRoller = async () => {
    try {
      const response = await fetchWithAuth('/api/personal/roll');
      if (!response.ok) throw new Error('Fel vid hämtning av roller');
      const data = await response.json();
      setRoller(data || []);
    } catch (error) {
      console.error('Fel vid hämtning av roller:', error);
    }
  };

  const fetchPersoner = async (station) => {
    if (!station || station === '---') return;
    setLoading(true);
    try {
      let url = '/api/personal/person';
      const params = new URLSearchParams();
      if (station) params.append('station', station);
      if (visaAllaStationer) params.append('allaStationer', 'true');

      const baseUrl = params.toString() ? `${url}?${params}` : url;

      // "aktuell=false" är en superset av "aktuell=true" (samma query, bara utan
      // AktuellRapport-filtret) — hämta den en gång och härled den aktuella delmängden
      // klientsidan via personens AktuellRapport-flagga, istället för två parallella anrop.
      const allaRes = await fetchWithAuth(`${baseUrl}${params.toString() ? '&' : '?'}aktuell=false`);

      if (!allaRes.ok) throw new Error('Fel vid hämtning av personer');

      const normalizePersonData = (personer) => personer.map(person => ({
        ...person,
        SITS: person.sits ?? false,
        C1: person.c1 ?? false,
        Ledning: person.ledning ?? false,
        MICU: person.micu ?? false,
        ALB: person.alb ?? false,
        JLB: person.jlb ?? false,
        PAM: person.pam ?? false,
        CBNRE: person.cbnre ?? false,
        NY: person.ny ?? false,
        rollNamn: person.rollNamn || ''
      }));

      const normalizedAlla = normalizePersonData(await allaRes.json() || []);
      const normalizedAktuella = normalizedAlla.filter(person => person.aktuellRapport);

      setAktuellaPersoner(normalizedAktuella);
      setAllaPersoner(normalizedAlla);

      setPersoner(visaAktuell ? normalizedAktuella : normalizedAlla);
    } catch (error) {
      console.error('Fel vid hämtning av personer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRollChange = async (personId, rollId) => {
    console.log("handleRollChange: ", { personId }, { rollId })
    setSavingPersonId(personId);
    try {
      const response = await fetchWithAuth(`/api/personal/person/roll`, {
        method: 'PUT',
        body: JSON.stringify({ personId: personId, rollId })
      });
      if (response.ok) {
        setPersoner(prev => prev.map(p =>
          p.id === personId
            ? { ...p, rollId, rollNamn: roller.find(r => r.id === rollId)?.rollNamn || p.rollNamn }
            : p
        ));
        setPersonChanged(prev => !prev);
      } else console.error('Fel vid uppdatering av roll');
    } catch (error) {
      console.error('Fel vid kommunikation med servern:', error);
    } finally { setSavingPersonId(null); }
  };

  const handleBooleanChange = async (personId, field, value) => {
    setSavingPersonId(personId);
    try {
      const payload = { PersonId: personId, field, value };
      const response = await fetchWithAuth(`/api/personal/person/${personId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setPersoner(prev => prev.map(p => p.id === personId ? { ...p, [field]: value } : p));
        setAktuellaPersoner(prev => prev.map(p => p.id === personId ? { ...p, [field]: value } : p));
        setAllaPersoner(prev => prev.map(p => p.id === personId ? { ...p, [field]: value } : p));
        setPersonChanged(prev => !prev);
      } else {
        const errorText = await response.text();
        console.error(`Fel vid uppdatering av ${field}:`, errorText);
        alert(`Kunde inte uppdatera ${field}. Försök igen.`);
      }
    } catch (error) {
      console.error('Fel vid kommunikation med servern:', error);
      alert('Ett fel uppstod. Försök igen.');
    } finally { setSavingPersonId(null); }
  };

  const handleDelete = async (person) => {
    const namn = person.namn?.trim() || '(namn saknas)';
    if (!window.confirm(`Är du säker på att ta bort "${namn}"?`)) return;
    setSavingPersonId(person.id);
    try {
      const response = await fetchWithAuth(`/api/personal/person/${person.id}`, { method: 'DELETE' });
      if (response.ok) {
        setPersoner(prev => prev.filter(p => p.id !== person.id));
        setAktuellaPersoner(prev => prev.filter(p => p.id !== person.id));
        setAllaPersoner(prev => prev.filter(p => p.id !== person.id));
        setPersonChanged(prev => !prev);
        toast.success(`${namn} borttagen`);
      } else {
        const data = await response.json().catch(() => null);
        toast.error(data?.message || `❌ Kunde inte ta bort ${namn}`);
      }
    } catch (error) {
      console.error('Fel vid kommunikation med servern:', error);
      toast.error(`❌ Kunde inte ta bort ${namn}`);
    } finally { setSavingPersonId(null); }
  };

  const sortBy = (key) => {
    setSortConfig(prev => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const filteredPersoner = useMemo(() => personer.filter(p =>
    p.namn.toLowerCase().includes(searchTerm.toLowerCase())
  ), [personer, searchTerm]);

  const sortedPersoner = useMemo(() => [...filteredPersoner].sort((a, b) => {
    const key = sortConfig.key;
    let aVal, bVal;
    if (key === 'roll') { aVal = a.rollNamn || ''; bVal = b.rollNamn || ''; }
    else if (typeof a[key] === 'boolean') { aVal = a[key] ? '1' : '0'; bVal = b[key] ? '1' : '0'; }
    else { aVal = a[key] || ''; bVal = b[key] || ''; }
    return sortConfig.direction === 'asc'
      ? aVal.toString().localeCompare(bVal.toString())
      : bVal.toString().localeCompare(aVal.toString());
  }), [filteredPersoner, sortConfig]);

  const getChevron = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ChevronUpIcon className="tw-h-4 tw-w-4 tw-inline-block tw-ml-1" />
      : <ChevronDownIcon className="tw-h-4 tw-w-4 tw-inline-block tw-ml-1" />;
  };

  const validRollIds = [1, 2, 3, 4, 5, 6];
  const validRollOptionIds = [1, 2, 3, 4, 5];

  const customSelectStyles = {
    control: (provided) => ({ ...provided, minHeight: '28px', height: '28px', fontSize: '12px', border: '1px solid #d1d5db', boxShadow: 'none' }),
    valueContainer: (provided) => ({ ...provided, height: '26px', padding: '0 8px' }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    menu: (provided) => ({ ...provided, fontSize: '12px' }),
    option: (provided) => ({ ...provided, padding: '4px 8px' }),
    singleValue: (provided) => ({ ...provided, fontSize: '12px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '0 4px' })
  };

  const handleKommandePass = async (personId) => {
    setSelectedPersonId(personId);
    await createKommandePass(personId, impersonatedUserId)
    console.log('Kommande pass för person:', personId);
  };

  const createKommandePass = useCallback(async (personId) => {
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    // Öppna fönstret DIREKT i klick-kontexten (innan await)
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      toast.error('❌ Popup blockerad av webbläsaren. Tillåt popups för denna sida.');
      setLoading(false);
      return;
    }

    // Visa laddningsindikator medan vi väntar
    newWindow.document.write(`
      <!DOCTYPE html>
      <html lang="sv">
      <head><meta charset="utf-8"><title>Laddar...</title></head>
      <body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;">
        <div style="text-align:center">
          <div style="font-size:24px;margin-bottom:16px;">⏳</div>
          <p>Genererar kommande pass...</p>
        </div>
      </body>
      </html>
    `);

    try {
      let urlLink = '/api/reports/kommandepass';
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: { 'Accept': 'text/html' },
        body: JSON.stringify({
          location: window.location.origin,
          station: station,
          type: 'kommandepass',
          personId: personId
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        newWindow.close();
        toast.error('❌ Fel vid generering av Kommande Pass-rapport');
        return;
      }

      // I createKommandePass, efter att html kommit tillbaka:
      const html = await response.text();

      const noDownload = html.includes('Arbetspass saknas');

      if (noDownload) {
        newWindow.document.open();
        newWindow.document.write(html);
        newWindow.document.close();
      } else {
        // Visa båda knapparna när arbetspass finns
        const downloadButton = `<div id="download-bar" style="position:fixed;top:10px;right:10px;z-index:9999;display:flex;gap:8px;">
                            <button id="btnDownload"
                              style="background:#2563eb;color:white;padding:8px 16px;border:none;border-radius:6px;
                                     cursor:pointer;font-size:14px;font-weight:bold;box-shadow:0 2px 4px rgba(0,0,0,0.2);">
                              📥 Ladda ner Excel
                            </button>
                          </div>`;

        const htmlWithButton = html.replace('</body>', downloadButton + '</body>');
        newWindow.document.open();
        newWindow.document.write(htmlWithButton);
        newWindow.document.close();

        newWindow.document.getElementById('btnDownload').addEventListener('click', () => {
          window.postMessage({ type: 'downloadExcel', personId: personId }, '*');
        });
      }

    } catch (error) {
      newWindow.close();
      if (error.name === 'AbortError') {
        toast.error('❌ Rapporten tog för lång tid att generera');
      } else {
        console.error('Fel:', error);
        toast.error('❌ Fel vid generering av rapport');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [selectedPersonId, station]);


  return (
    <div className="tw-border tw-border-black tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-lg tw-w-full tw-mx-12">
      <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Personal</h2>
      {/* Stationsfilter */}
      <div className="tw-mb-4">
        <label className="tw-mr-4">
          <input
            type="radio"
            name="stationFilter"
            value="station"
            checked={!visaAllaStationer}
            onChange={() => setVisaAllaStationer(false)}
            className="tw-mr-1"
          />
          &nbsp;Personal på {station}
        </label>
        <label>
          <input
            type="radio"
            name="stationFilter"
            value="alla"
            checked={visaAllaStationer}
            onChange={() => setVisaAllaStationer(true)}
            className="tw-mr-1"
          />
          &nbsp;Personal alla stationer
        </label>
      </div>

      {/* Radioknappar */}
      <div className="tw-mb-4">
        <label className="tw-mr-4">
          <input type="radio" name="personFilter" value="all" checked={!visaAktuell} onChange={() => setVisaAktuell(false)} className="tw-mr-1" />
          &nbsp;All personal ({allaPersoner.length})
        </label>
        <label>
          <input type="radio" name="personFilter" value="aktuell" checked={visaAktuell} onChange={() => setVisaAktuell(true)} className="tw-mr-1" />
          &nbsp;Personal i senaste heromaimport ({aktuellaPersoner.length})
        </label>
      </div>

      {/* Sökfält med live-filter och automatiskt rensa-kryss */}
      <div className="tw-mb-4">
        <input
          type="search"  // ← Detta är nyckeln!
          placeholder="Sök namn..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-full"
        />
      </div>

      {/* Tabell */}
      <div className="tw-overflow-x-auto tw-overflow-y-auto tw-max-h-[500px] tw-rounded-lg tw-shadow-inner tw-pb-5">
        <table className="tw-w-full tw-text-sm tw-table-auto tw-border-collapse tw-mb-3">
          <thead className="tw-sticky tw-top-0 tw-bg-gray-100 tw-z-10 tw-shadow-sm">
            <tr className="tw-text-left">
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200">
                <button onClick={() => sortBy('namn')} className="tw-bg-yellow-500 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-purple-700 hover:tw-text-white">
                  Namn {getChevron('namn')}
                </button>
              </th>
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200"></th>
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200 tw-w-28">
                <button onClick={() => sortBy('station')} className="tw-bg-yellow-500 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-purple-700 hover:tw-text-white">
                  Station {getChevron('station')}
                </button>
              </th>
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200 tw-w-28">
                <button onClick={() => sortBy('roll')} className="tw-bg-yellow-500 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-purple-700 hover:tw-text-white">
                  Roll {getChevron('roll')}
                </button>
              </th>
              {/* Boolean columns */}
              {['SITS', 'C1', 'Ledning', 'MICU', 'ALB', 'JLB', 'PAM', 'CBNRE', 'NY'].map(field => (
                <th key={field} className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200 tw-text-center">
                  <button onClick={() => sortBy(field)} className="tw-bg-yellow-500 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-purple-700 hover:tw-text-white">
                    {field} {getChevron(field)}
                  </button>
                </th>
              ))}
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200 tw-whitespace-nowrap">
                <button onClick={() => sortBy('usedInSchemaAt')} className="tw-bg-yellow-500 tw-text-blue-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-purple-700 hover:tw-text-white">
                  Senast i Schema {getChevron('usedInSchemaAt')}
                </button>
              </th>
              <th className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200"></th>
            </tr>
          </thead>
          <tbody>
            {sortedPersoner.map((person, index) => (
              <tr
                key={person.id}
                className={`${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'} ${savingPersonId === person.id ? 'tw-opacity-50' : ''
                  }`}
              >
                <td className="tw-px-2 tw-py-2 tw-text-gray-800 tw-border-b tw-border-gray-200">{person.namn}</td>
                <td className="tw-px-1 tw-py-2 tw-border-b tw-border-gray-200">
                  <button
                    onClick={() => handleKommandePass(person.id)}
                    className="tw-bg-yellow-500 tw-text-white tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-yellow-700 tw-whitespace-nowrap"
                  >
                    Kommande Pass
                  </button>
                </td>
                <td className="tw-px-2 tw-py-2 tw-text-gray-800 tw-border-b tw-border-gray-200">{person.station}</td>
                <td className="tw-px-2 tw-py-2 tw-border-b tw-border-gray-200">
                  <Select
                    value={
                      roller
                        .filter(r => validRollIds.includes(r.id))
                        .find(r => r.id === person.rollId)
                        ? { value: person.rollId, label: person.rollNamn }
                        : null
                    }
                    onChange={option => handleRollChange(person.id, option.value)}
                    options={roller
                      .filter(r => validRollOptionIds.includes(r.id))
                      .map(r => ({ value: r.id, label: r.rollNamn }))}
                    styles={customSelectStyles}
                    placeholder="Välj roll..."
                    isDisabled={savingPersonId === person.id}
                    className="tw-text-xs"
                  />
                </td>
                {['SITS', 'C1', 'Ledning', 'MICU', 'ALB', 'JLB', 'PAM', 'CBNRE', 'NY'].map(field => (
                  <td key={field} className="tw-px-2 tw-py-2 tw-text-center tw-border-b tw-border-gray-200">
                    <input
                      type="checkbox"
                      checked={!!person[field]}
                      onChange={e => handleBooleanChange(person.id, field, e.target.checked)}
                      disabled={savingPersonId === person.id}
                      className="tw-w-4 tw-h-4 tw-cursor-pointer"
                    />
                  </td>
                ))}
                <td className="tw-px-2 tw-py-2 tw-text-gray-800 tw-border-b tw-border-gray-200 tw-whitespace-nowrap">
                  {person.usedInSchemaAt ? person.usedInSchemaAt.slice(0, 10) : ''}
                </td>
                <td className="tw-px-1 tw-py-2 tw-border-b tw-border-gray-200">
                  {person.kanTasBort && (
                    <button
                      onClick={() => handleDelete(person)}
                      disabled={savingPersonId === person.id}
                      title={person.namn?.trim() ? 'Inaktiv i mer än 3 månader och inte med i något framtida schema' : 'Person utan namn'}
                      className="tw-bg-red-600 tw-text-white tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-bold hover:tw-bg-red-800 tw-whitespace-nowrap"
                    >
                      Ta bort
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PersonalForm;
